import { DEFAULT_CURRENCY } from "@/lib/format/money";
import { apiRequest } from "@/lib/api/http";
import {
  costDate,
  costNullableNumber,
  costNullableText,
  costText,
  oneOf,
} from "@/lib/api/cost-fields";
import type { CostDue, CostDueSeverity } from "@/types/fleet";

const SEVERITIES = [
  "overdue",
  "due_soon",
  "upcoming",
] as const satisfies readonly CostDueSeverity[];

/** Matches the upstream default for `?within_days=`. */
export const DEFAULT_DUE_WINDOW_DAYS = 30;

interface RemoteCostDue {
  id: string;
  cost_recurrence_id?: string;
  cost_id?: string | null;
  client_device_id?: string;
  cost_category_id?: string;
  title?: string;
  due_date?: string | null;
  due_odometer?: string | null;
  days_remaining?: string | null;
  km_remaining?: string | null;
  estimated_amount?: string | null;
  currency?: string;
  severity?: string;
}

interface ListResponse<T> {
  success: true;
  data: T[];
}

function mapCostDue(due: RemoteCostDue): CostDue {
  return {
    id: due.id,
    recurrenceId: costText(due.cost_recurrence_id),
    costId: costNullableText(due.cost_id),
    vehicleId: costText(due.client_device_id),
    categoryId: costText(due.cost_category_id),
    title: costText(due.title),
    dueDate: costDate(due.due_date) || null,
    dueOdometer: costNullableNumber(due.due_odometer),
    daysRemaining: costNullableNumber(due.days_remaining),
    kmRemaining: costNullableNumber(due.km_remaining),
    estimatedAmount: costNullableNumber(due.estimated_amount),
    currency: costText(due.currency) || DEFAULT_CURRENCY,
    severity: oneOf(due.severity, SEVERITIES, "upcoming"),
  };
}

export async function listCostDue(
  withinDays: number = DEFAULT_DUE_WINDOW_DAYS,
): Promise<CostDue[]> {
  const response = await apiRequest<ListResponse<RemoteCostDue>>(
    `/api/cost-due?within_days=${withinDays}`,
    { method: "GET", cache: "no-store" },
  );
  return response.data.map(mapCostDue);
}
